import {
  ApiUrls,
  AppMessages,
  SessionStorageKeys,
  ValidationMessages,
} from "./constants";
import { json, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { UserCookie } from "../utils/constants";
import { routeNames } from "../routes/routes";
import moment from "moment";
import config from "../config.json";
import CryptoJS from "crypto-js";
import { axiosPost } from "../helpers/axiosHelper";
import { getsessionStorageItem } from "../helpers/sessionStorageHelper";

const TOP_PAGE_LOADER_HIDE_TIMER_KEY = "awTopPageLoaderHideTimer";
const API_ACTION_PROCESSING_CLASS = "api-action-processing";
const API_ACTION_OVERLAY_ID = "aw-api-action-overlay";

let apiActionProcessingCount = 0;

function createApiActionOverlay(
  titleText = "Submitting your request...",
  subtitleText = "Please wait a moment",
) {
  if (typeof document === "undefined" || !document.body) {
    return;
  }

  const existingOverlay = document.getElementById(API_ACTION_OVERLAY_ID);
  if (existingOverlay) {
    const existingTitle = existingOverlay.querySelector(".aw-api-action-title");
    const existingSubtitle = existingOverlay.querySelector(
      ".aw-api-action-subtitle",
    );
    if (existingTitle) existingTitle.textContent = titleText;
    if (existingSubtitle) existingSubtitle.textContent = subtitleText;
    return;
  }

  let overlay = document.createElement("div");
  overlay.id = API_ACTION_OVERLAY_ID;
  overlay.className = "aw-api-action-overlay";
  overlay.setAttribute("role", "status");
  overlay.setAttribute("aria-live", "polite");

  let card = document.createElement("div");
  card.className = "aw-api-action-card";

  let dots = document.createElement("div");
  dots.className = "aw-api-action-dots";
  dots.setAttribute("aria-hidden", "true");
  for (let i = 0; i < 3; i += 1) {
    dots.appendChild(document.createElement("span"));
  }

  let title = document.createElement("div");
  title.className = "aw-api-action-title";
  title.textContent = titleText;

  let subtitle = document.createElement("div");
  subtitle.className = "aw-api-action-subtitle";
  subtitle.textContent = subtitleText;

  card.appendChild(dots);
  card.appendChild(title);
  card.appendChild(subtitle);
  overlay.appendChild(card);
  document.body.appendChild(overlay);
}

function removeApiActionOverlay() {
  document.getElementById(API_ACTION_OVERLAY_ID)?.remove();
}

function setApiActionProcessing(isProcessing, titleText, subtitleText) {
  if (typeof document === "undefined" || !document.body) {
    return;
  }

  if (isProcessing) {
    apiActionProcessingCount += 1;
    document.documentElement.classList.add(API_ACTION_PROCESSING_CLASS);
    document.body.classList.add(API_ACTION_PROCESSING_CLASS);
    document.body.setAttribute("aria-busy", "true");
    createApiActionOverlay(titleText, subtitleText);
    return;
  }

  apiActionProcessingCount = Math.max(0, apiActionProcessingCount - 1);
  if (apiActionProcessingCount === 0) {
    document.documentElement.classList.remove(API_ACTION_PROCESSING_CLASS);
    document.body.classList.remove(API_ACTION_PROCESSING_CLASS);
    document.body.removeAttribute("aria-busy");
    removeApiActionOverlay();
  }
}

function startTopPageLoader() {
  let $ = window.$;
  let $loader = $(".preloader");
  if (!$loader.length) {
    return;
  }

  let hideTimer = $loader.data(TOP_PAGE_LOADER_HIDE_TIMER_KEY);
  if (hideTimer) {
    clearTimeout(hideTimer);
    $loader.removeData(TOP_PAGE_LOADER_HIDE_TIMER_KEY);
  }

  $loader
    .stop(true, true)
    .css("opacity", "")
    .show()
    .removeClass("is-loading is-complete");
  void $loader[0].offsetWidth;
  $loader.addClass("is-loading");
}

function completeTopPageLoader(delay = 0) {
  let $ = window.$;
  let $loader = $(".preloader");
  if (!$loader.length) {
    return;
  }

  let hideTimer = $loader.data(TOP_PAGE_LOADER_HIDE_TIMER_KEY);
  if (hideTimer) {
    clearTimeout(hideTimer);
  }

  hideTimer = setTimeout(() => {
    $loader
      .stop(true, true)
      .removeClass("is-loading")
      .addClass("is-complete")
      .show();

    let fadeTimer = setTimeout(() => {
      $loader.removeClass("is-complete");

      let displayTimer = setTimeout(() => {
        if (
          !$loader.hasClass("is-loading") &&
          !$loader.hasClass("is-complete")
        ) {
          $loader.hide();
        }
        $loader.removeData(TOP_PAGE_LOADER_HIDE_TIMER_KEY);
      }, 140);

      $loader.data(TOP_PAGE_LOADER_HIDE_TIMER_KEY, displayTimer);
    }, 220);

    $loader.data(TOP_PAGE_LOADER_HIDE_TIMER_KEY, fadeTimer);
  }, delay);

  $loader.data(TOP_PAGE_LOADER_HIDE_TIMER_KEY, hideTimer);
}

/** set page loader on mounting and unmounitng and nav links active.inactive */
export function SetPageLoaderNavLinks() {
  let $ = window.$;
  let location = useLocation();

  useEffect(() => {
    handlePreLoader();
    setNavLinksActive();
    return () => {
      startTopPageLoader();
    };
  }, []);

  function handlePreLoader() {
    completeTopPageLoader(250);
  }

  function setNavLinksActive() {
    $(".navbar-nav .nav-item").removeClass("active");
    let current = location.pathname.toLowerCase();
    if (current.indexOf("user/") != -1) {
      let activelink = "";
      let activesublink = "";
      switch (current) {
        case routeNames.agentmyproperties.path.toLowerCase():
          activelink = "properties";
          activesublink = "agentproperties";
          break;
        case routeNames.agentviewproperty.path.toLowerCase():
        case routeNames.tenantviewproperty.path.toLowerCase():
          activelink = "properties";
          break;
        case routeNames.addresidentialproperty.path.toLowerCase():
          activelink = "properties";
          activesublink = "addresidentialproperty";
          break;
        case routeNames.addcommercialproperty.path.toLowerCase():
          activelink = "properties";
          activesublink = "addcommercialproperty";
          break;
        case routeNames.manageresidentialproperty.path.toLowerCase():
        case routeNames.managecommercialproperty.path.toLowerCase():
          activelink = "properties";
          activesublink = "addresidentialproperty";
          break;
        case routeNames.agentassignedproperties.path.toLowerCase():
        case routeNames.manageagentresidentialproperty.path.toLowerCase():
        case routeNames.manageagentcommercialproperty.path.toLowerCase():
          activelink = "properties";
          activesublink = "assignedproperties";
          break;
        case routeNames.tenantconnectedproperties.path.toLowerCase():
          activelink = "properties";
          activesublink = "connectedproperties";
          break;
        case routeNames.settings.path.toLowerCase():
        case routeNames.branding.path.toLowerCase():
          activelink = "settings";
          break;
        case routeNames.ownersmyproperties.path.toLowerCase():
          activelink = "properties";
          activesublink = "ownerproperties";
          break;
        case routeNames.listedproperties.path.toLowerCase():
          activelink = "properties";
          activesublink = "listedproperties";
          break;
        case routeNames.partnershipproperties.path.toLowerCase():
          activelink = "properties";
          activesublink = "partnershipproperties";
          break;
        case routeNames.addnewproperty.path.toLowerCase():
          activelink = "properties";
          activesublink = "addnewproperty";
          break;
        case routeNames.listproperty.path.toLowerCase():
          activelink = "properties";
        case routeNames.ownerviewproperty.path.toLowerCase():
          activelink = "properties";
          break;
        // case routeNames.agentassignedproperties.path.toLowerCase():
        // case routeNames.agentviewproperty.path.toLowerCase():
        //   activelink = "properties";
        //   activesublink = "assignedproperties";
        //   break;
        case routeNames.tenantproperties.path.toLocaleLowerCase():
          activelink = "properties";
        case routeNames.connections.path.toLowerCase():
          activelink = "myconnections";
          activesublink = "myconnections";
        case routeNames.connectionssendmessage.path.toLowerCase():
        case routeNames.editconnection.path.toLowerCase():
          activelink = "myconnections";
          break;
        case routeNames.connectionssendinvite.path.toLowerCase():
          activelink = "myconnections";
          activesublink = "newconnection";
          break;
        case routeNames.mygroups.path.toLowerCase():
        case routeNames.creategroup.path.toLowerCase():
        case routeNames.groupaddmembers.path.toLowerCase():
        case routeNames.viewgroup.path.toLowerCase():
        case routeNames.groupsendmessage.path.toLowerCase():
        case routeNames.groupmessages.path.toLowerCase():
        case routeNames.viewgroupmessage.path.toLowerCase():
          activelink = "myconnections";
          activesublink = "mygroups";
          break;
        case routeNames.paymentsaccounts.path.toLowerCase():
        case routeNames.paymentscreateaccount.path.toLowerCase():
        case routeNames.paymentsaccountagreement.path.toLowerCase():
          activelink = "payments";
          activesublink = "paymentaccounts";
          break;
        case routeNames.paymentspaymentcards.path.toLowerCase():
          activelink = "payments";
          activesublink = "paymentpaymentcards";
          break;
        case routeNames.paymentsinvoices.path.toLowerCase():
        case routeNames.invoiceitems.path.toLowerCase():
        case routeNames.createinvoiceitem.path.toLowerCase():
        case routeNames.viewinvoiceitem.path.toLowerCase():
        case routeNames.createinvoice.path.toLowerCase():
        case routeNames.previewinvoice.path.toLowerCase():
        case routeNames.viewinvoice.path.toLowerCase():
        case routeNames.editinvoice.path.toLowerCase():
        case routeNames.sendinvoice.path.toLowerCase():
        case routeNames.markaspaid.path.toLowerCase():
        case routeNames.usercheckout.path.toLowerCase():
          activelink = "payments";
          activesublink = "paymentinvoices";
          break;
        case routeNames.tenantprofile.path.toLowerCase():
        case routeNames.agentprofile.path.toLowerCase():
        case routeNames.ownerprofile.path.toLowerCase():
        case routeNames.tenantprofileedit.path.toLowerCase():
        case routeNames.agentprofileedit.path.toLowerCase():
        case routeNames.ownerprofileedit.path.toLowerCase():
          activelink = "user-profile";
          break;
        case routeNames.myagreements.path.toLowerCase():
        case routeNames.myagreementssent.path.toLowerCase():
        case routeNames.myagreementsreceived.path.toLowerCase():
        case routeNames.myagreementsexecuted.path.toLowerCase():
        case routeNames.createagreement.path.toLowerCase():
        case routeNames.manageagreementaccess.path.toLowerCase():
          activelink = "agreements";
          activesublink = "myagreements";
          break;
        case routeNames.sendagreement.path.toLowerCase():
        case routeNames.previewagreement.path.toLowerCase():
        case routeNames.templatestore.path.toLowerCase():
          activelink = "agreements";
          activesublink = "templatestore";
          break;
        case routeNames.mytemplates.path.toLowerCase():
        case routeNames.createtemplate.path.toLowerCase():
          activelink = "agreements";
          activesublink = "mytemplates";
          break;
        case routeNames.viewdocument.path.toLowerCase():
        case routeNames.sharedfolders.path.toLowerCase():
        case routeNames.sharedusers.path.toLowerCase():
          activelink = "documents";
          break;
        case routeNames.reportsettings.path.toLowerCase():
        case routeNames.propertiestransactions.path.toLowerCase():
        case routeNames.accounttransactions.path.toLowerCase():
          activelink = "reports";
          break;
        case routeNames.mydocuments.path.toLowerCase():
        case routeNames.managedocumentaccess.path.toLowerCase():
        case routeNames.sharedocument.path.toLowerCase():
          activelink = "documents";
          activesublink = "mydocuments";
          break;
        case routeNames.shareddocuments.path.toLowerCase():
          activelink = "documents";
          activesublink = "shareddocuments";
          break;
        case routeNames.services.path.toLowerCase():
          activelink = "services";
          activesublink = "myservices";
          break;
        case routeNames.createservicerequest.path.toLowerCase():
          activelink = "services";
          activesublink = "servicerequest";
        case routeNames.servicedetails.path.toLowerCase():
        case routeNames.assignrequest.path.toLowerCase():
        case routeNames.assignservices.path.toLowerCase():
        case routeNames.editservice.path.toLowerCase():
        case routeNames.sendservicerequest.path.toLowerCase():
          activelink = "services";
          break;
        case routeNames.viewapplication.path.toLowerCase():
        case routeNames.viewapplicationform.path.toLowerCase():
        case routeNames.applicationform.path.toLowerCase():
        case routeNames.sendapplication.path.toLowerCase():
        case routeNames.applications.path.toLowerCase():
          activelink = "applications";
          activesublink = "myapplications";
          break;
        case routeNames.createapplication.path.toLowerCase():
          activelink = "applications";
          activesublink = "createapplication";
          break;
        case routeNames.businesses.path.toLowerCase():
        case routeNames.viewbusiness.path.toLowerCase():
        case routeNames.businessdetails.path.toLowerCase():
        case routeNames.editbusiness.path.toLowerCase():
        case routeNames.editprofilebusiness.path.toLowerCase():
        case routeNames.businessmembers.path.toLowerCase():
        case routeNames.businessaddmember.path.toLowerCase():
        case routeNames.businesseditmember.path.toLowerCase():
        case routeNames.businessviewmember.path.toLowerCase():
        case routeNames.listbusiness.path.toLowerCase():
        case routeNames.reviewprofile.path.toLowerCase():
          activelink = "business";
          activesublink = "mybusiness";
          break;
        case routeNames.addbusiness.path.toLowerCase():
          activelink = "business";
          activesublink = "createbusiness";
          break;
      }

      $('[id^="nav-"],[id^="nav-lnk-"],[id^="page-lnk-"]')
        .parent("li")
        .removeClass("active");

      $(
        `#nav-${activelink},
        #nav-lnk-${activesublink},
        #page-lnk-${activesublink}`,
      )
        .parent("li")
        .addClass("active");
    } else if (current.indexOf("/property/") != -1) {
      $("#nav-properties").parent("li").addClass("active");
    } else {
      let path = current.substring(current.lastIndexOf("/") + 1);
      switch (path.toLowerCase()) {
        case "/":
        case "home":
        case "":
        default:
          path = "home";
          break;
        case "aboutus":
          path = "aboutus";
          break;
        case "contactus":
          path = "contactus";
          break;
        case "signin":
        case "forgotpassword":
          path = "login";
          break;
        case "signup":
        case "accountinfo":
        case "accounttype":
        case "profiletype":
        case "businessinfo":
        case "services":
          path = "register";
          break;
        case "properties":
          path = "properties";
          break;
        case "plans":
          path = "plans";
          break;
        case "blog":
          path = "blog";
          break;
        case "partners":
          path = "partners";
          break;
        case "testimonials":
          path = "testimonials";
          break;
        case "createtestimonial":
          path = "createtestimonial";
          break;
        case "howitworks":
          path = "howitworks";
          break;
      }

      $("#nav-" + path)
        .parent("li")
        .addClass("active");
    }
  }
}

/** set page loader on mounting and unmounitng and nav links active.inactive */
export function SetUserMenu() {
  let $ = window.$;

  useEffect(() => {
    handlePreLoader();
    closeCollapse();
    closebsDropDown();

    return () => {
      // if ($(".preloader").length) {
      //   $(".preloader").show().delay(300).fadeOut(300);
      // }
      closeCollapse();
      closebsDropDown();
    };
  }, []);

  function handlePreLoader() {
    // if ($(".preloader").length) {
    //   $(".preloader").delay(300).fadeOut(300);
    // }
  }

  $(document)
    .off("click")
    .on("click", function (event) {
      document.querySelectorAll(".collapse").forEach(function (collapse) {
        var bsCollapse = new window.bootstrap.Collapse(collapse, {
          toggle: false,
          //container: "body",
        });
        bsCollapse.hide();
      });

      if (!$(event.target).closest(".gr-action-btn").length) {
        closeAllBSDropDowns();
      } else {
        let closestBtn = $(event.target).closest(".gr-action-btn");
        if ($(closestBtn).attr("aria-expanded") == "true") {
          $(closestBtn).attr("aria-expanded", "false");
          $(closestBtn).next(".gr-action-menu").slideUp();
        } else {
          closeAllBSDropDowns();
          // $(closestBtn).attr("aria-expanded", "true");
          // $(closestBtn).next(".gr-action-menu").slideDown();
          let menu = $(closestBtn).next(".gr-action-menu");
          const rect = closestBtn[0].getBoundingClientRect();

          const menuHeight = menu.outerHeight() || 200;
          const spaceBelow = window.innerHeight - rect.bottom;

          const buffer = 100;
          if (spaceBelow < menuHeight + buffer) {
            menu.addClass("open-up");
          } else {
            menu.removeClass("open-up");
          }
          $(closestBtn).attr("aria-expanded", "true");
          menu.slideDown();
        }
      }
    });

  function closeAllBSDropDowns() {
    $(".gr-action-btn").attr("aria-expanded", "false");
    $(".gr-action-menu").slideUp();
  }

  function closeCollapse() {
    document
      .querySelectorAll('[data-bs-toggle="collapse"]')
      .forEach(function (element) {
        element.addEventListener("click", function (event) {
          // Get the target collapse
          var target = document.querySelector(
            this.getAttribute("data-bs-target"),
          );
          // Close all other collapses
          document.querySelectorAll(".collapse").forEach(function (collapse) {
            if (collapse !== target) {
              var bsCollapse = new window.bootstrap.Collapse(collapse, {
                toggle: false,
                //container: "body",
              });
              bsCollapse.hide();
            }
          });
        });
      });
  }

  function closebsDropDown() {
    let $window = $(window),
      $navigation = $("#navbarSupportedContent"),
      $widgetusernavlinks = $("#collpase-widget-usernavlinks"),
      $dropdown = $(".dropdown-toggle");

    ($navigation, $widgetusernavlinks).each(function () {
      $dropdown.on("click", function (e) {
        // if ($window.width() < 1100) {
        if ($(this).parent(".dropdown").hasClass("visible")) {
          $(this)
            .parent(".dropdown")
            .children(".dropdown-menu")
            .first()
            .stop(true, true)
            .slideUp(300);
          $(this).parent(".dropdown").removeClass("visible");
        } else {
          e.preventDefault();
          $(this)
            .parent(".dropdown")
            .siblings(".dropdown")
            .children(".dropdown-menu")
            .slideUp(300);
          $(this)
            .parent(".dropdown")
            .siblings(".dropdown")
            .removeClass("visible");
          $(this).parent(".dropdown").children(".dropdown-menu").slideDown(300);
          $(this).parent(".dropdown").addClass("visible");
        }
        e.stopPropagation();
        // }
      });
    });
  }
}

export function SetAccordion() {
  let $ = window.$;

  useEffect(() => {
    // Custom accordion useable settings for any type of accordion system
    if (document.querySelector(".bb-accordion") !== null) {
      $(".ac-toggle").on("click", function (e) {
        e.preventDefault();

        var $this = $(this);

        if ($this.hasClass("active") && $this.next().hasClass("show")) {
          $this.removeClass("active");
          $this.next().removeClass("show");
          $this.next().slideUp(350);
        } else {
          // Check accordion type: for single item open
          if ($this.parent().parent().hasClass("ac-single-show")) {
            $this
              .parent()
              .parent()
              .find(".ac-card .ac-toggle")
              .removeClass("active");
            $this
              .parent()
              .parent()
              .find(".ac-card .ac-collapse")
              .removeClass("show");
            $this.parent().parent().find(".ac-card .ac-collapse").slideUp(350);
            $this.addClass("active");
            $this.next().addClass("show");
            $this.next().slideDown(350);
          }

          // Check accordion type: for group item open
          else if ($this.parent().parent().hasClass("ac-group-show")) {
            $this.addClass("active");
            $this.next().addClass("show");
            $this.next().slideDown(350);
          }

          // Default if not use any accordion type
          else {
            $this
              .parent()
              .parent()
              .find(".ac-card .ac-toggle")
              .removeClass("active");
            $this
              .parent()
              .parent()
              .find(".ac-card .ac-collapse")
              .removeClass("show");
            $this.parent().parent().find(".ac-card .ac-collapse").slideUp(350);
            $this.addClass("active");
            $this.next().addClass("show");
            $this.next().slideDown(350);
          }
        }
      });
    }
  }, []);
}

/** check empty val empty return true / false */
export function checkEmptyVal(val) {
  if (val === null || val === undefined || val === "") return true;
  else return false;
}

/** check empty object empty return true / false */
export function checkObjNullorEmpty(obj) {
  if (
    obj === null ||
    obj === undefined ||
    obj === "" ||
    Object.keys(obj).length === 0
  )
    return true;
  else return false;
}

/** set select control default value  */
export function setSelectDefaultVal(obj, defaultVal = 0) {
  if (typeof obj === "object") {
    //check obj type
    if (checkObjNullorEmpty(obj)) {
      // check obj empty
      return defaultVal;
    } else {
      return obj.value;
    }
  } else {
    if (checkEmptyVal(obj)) {
      // check val empty
      return defaultVal;
    } else {
      return obj;
    }
  }
}

/** set page api request action */
export function apiReqResLoader(
  btnid,
  text,
  action = "start",
  showPageLoader = true,
  isbtnicon = false,
  processingTitle,
  processingSubtitle,
) {
  let $ = window.$;
  let normalizedBtnId = String(btnid || "x").toLowerCase();
  let hasActionButton = normalizedBtnId !== "x";

  if (action.toLowerCase() === "start") {
    if (hasActionButton) {
      setApiActionProcessing(true, processingTitle, processingSubtitle);
      //   $("#" + btnid)
      //     .html(text + ' <i class="fa fa-spinner fa-spin"></i>')
      //     .attr("disabled", "disabled");
      $("#" + btnid).html(
        text +
          '  <span class="dots"><span class="dot"></span><span class="dot"></span><span class="dot"></span></span>',
      );
      if (isbtnicon) {
        $("#" + btnid)
          .parent()
          .attr("disabled", "disabled");
      } else {
        $("#" + btnid).attr("disabled", "disabled");
      }
    }
    if (showPageLoader && $(".preloader").length) {
      startTopPageLoader();
    }
  } else {
    if (hasActionButton) {
      $("#" + btnid).html(text);
      if (!isbtnicon) {
        $("#" + btnid).removeAttr("disabled");
      } else {
        $("#" + btnid)
          .parent()
          .removeAttr("disabled");
      }
      setApiActionProcessing(false);
    }
    if (showPageLoader && $(".preloader").length) {
      completeTopPageLoader(150);
    }
  }
}

/** set select control loading */
export function setDdlLoading(ctrlid, text = AppMessages.DdLLoading) {
  let $ = window.$;
  $("#" + ctrlid)
    .empty()
    .append(`<option>${text}</option>`);
}

/** set select control loading */
export function setDdlNoData(ctrlid, val = "", text = AppMessages.DdlNoData) {
  let $ = window.$;
  $("#" + ctrlid)
    .empty()
    .append(
      `<option ${!checkEmptyVal(val) ? `value = ${val}` : ""}>${text}</option>`,
    );
}

/** set select control empty */
export function setDdlEmpty(ctrlid) {
  let $ = window.$;
  $("#" + ctrlid).empty();
}

/** set show hide Ctrl */
export function showHideCtrl(ctrlid, ishide = false) {
  let $ = window.$;
  ishide ? $("#" + ctrlid).hide() : $("#" + ctrlid).show();
}

export function checkStartEndDateGreater(start, end) {
  if (moment(end).isSame(moment(start))) {
    return "";
  } else {
    return moment(end).isAfter(moment(start)) == false
      ? ValidationMessages.StartEndDateGreater
      : "";
  }
}

/** sconvvert string to bool */
export function stringToBool(e) {
  switch (e?.toLowerCase()?.trim()) {
    case "true":
    case "yes":
    case "1":
      return !0;
    case "false":
    case "no":
    case "0":
    case null:
    case void 0:
      return !1;
    default:
      return e;
  }
}

export function GetCookieValues(cookiekey) {
  const { loggedinUser } = useAuth();
  return readCookieVal(cookiekey, loggedinUser);
}

export function GetUserCookieValues(cookiekey, user = {}) {
  return readCookieVal(cookiekey, user);
}

function readCookieVal(cookiekey, user) {
  let retval = "";
  switch (cookiekey) {
    case UserCookie.AccountId:
      retval = user?.[UserCookie.AccountId];
      break;
    case UserCookie.SessionId:
      retval = user?.[UserCookie.SessionId];
      break;
    case UserCookie.ProfilePic:
      retval = user?.[UserCookie.Profile][UserCookie.ProfilePic];
      break;
    case UserCookie.Email:
      retval = user?.[UserCookie.Email];
      break;
    case UserCookie.PlanId:
      retval = user?.[UserCookie.PlanId];
      break;
    case UserCookie.Plan:
      retval = user?.[UserCookie.Plan];
      break;
    case UserCookie.IsSetupCompleted:
      retval = user?.[UserCookie.IsSetupCompleted];
      break;
    case UserCookie.Name:
      retval = user?.[UserCookie.Profile][UserCookie.Name];
      break;
    case UserCookie.ProfileType:
      retval = user?.[UserCookie.Profile][UserCookie.ProfileType];
      break;
    case UserCookie.ProfileTypeId:
      retval = user?.[UserCookie.Profile][UserCookie.ProfileTypeId];
      break;
    case UserCookie.ProfileCategoryIds:
      retval = user?.[UserCookie.Profile][UserCookie.ProfileCategoryIds];
      break;
    case UserCookie.ProfileId:
      retval = user?.[UserCookie.Profile]?.[UserCookie.ProfileId];
      break;
    case UserCookie.AccountTypeId:
      retval = user?.[UserCookie.Profile][UserCookie.AccountTypeId];
      break;
    case UserCookie.AccountType:
      retval = user?.[UserCookie.Profile][UserCookie.AccountType];
      break;
    case UserCookie.CountryId:
      retval = user?.[UserCookie.Profile][UserCookie.CountryId];
      break;
    default:
      retval = "";
      break;
  }
  return retval;
}

export function setDdlOptions(
  ctrlname,
  options,
  dataKey,
  dataValue,
  isSelectOption = true,
) {
  let $ = window.$;
  let ctrl = $(`[name=${ctrlname}]`);
  ctrl.empty();
  if (typeof options === "string" && options?.toLowerCase() == "loading") {
    ctrl.append(
      $("<option/>", {
        value: "0",
        text: "Loading...",
      }),
    );
  } else {
    if (isSelectOption == true) {
      ctrl.append(
        $("<option/>", {
          value: "0",
          text: "Select",
        }),
      );
    }
    $.each(options, (idx, item) => {
      ctrl.append(
        $("<option/>", {
          value: item[dataKey],
          text: item[dataValue],
        }),
      );
    });
  }
}

export function replacePlaceHolders(template, data) {
  return template.replace(/{(.*?)}/g, (match, key) => data[key.trim()] || "");
}

export const debounce = (func, delay) => {
  let timeout;
  return (...args) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      func(...args);
    }, delay);
  };
};

export function getPagesPathByLoggedinUserProfile(
  loggedinProfileTypeId,
  page = "",
) {
  let path = "#";
  if (checkEmptyVal(page) == false) {
    if (page?.toLowerCase() == "notifications") {
      // path =
      //   loggedinProfileTypeId == config.userProfileTypes.Owner
      //     ? routeNames.ownernotifications.path
      //     : loggedinProfileTypeId == config.userProfileTypes.Agent
      //     ? routeNames.agentnotifications.path
      //     : loggedinProfileTypeId == config.userProfileTypes.Tenant
      //     ? routeNames.tenantnotifications.path
      //     : path;
      path = routeNames.notifications.path;
    } else if (page?.toLowerCase() == "profile") {
      path =
        loggedinProfileTypeId == config.userProfileTypes.Owner
          ? routeNames.ownerprofile.path
          : loggedinProfileTypeId == config.userProfileTypes.Agent
            ? routeNames.agentprofile.path
            : loggedinProfileTypeId == config.userProfileTypes.Tenant
              ? routeNames.tenantprofile.path
              : path;
    } else if (page?.toLowerCase() == "profileedit") {
      path =
        loggedinProfileTypeId == config.userProfileTypes.Owner
          ? routeNames.ownerprofileedit.path
          : loggedinProfileTypeId == config.userProfileTypes.Agent
            ? routeNames.agentprofileedit.path
            : loggedinProfileTypeId == config.userProfileTypes.Tenant
              ? routeNames.tenantprofileedit.path
              : path;
    }
    return path;
  }
}

export const usioGetToken = (objCard, objUser) => {
  return new Promise((resolve, reject) => {
    axiosPost(`${config.apiBaseUrl}${ApiUrls.getPaymentSubAccountDetails}`, {
      SubAccountId: objUser.SubAccountId,
      AccountId: objUser.AccountId,
      ProfileId: objUser.ProfileId,
      BusinessId: objUser.BusinessId,
      IsAccountLevel: 1,
      Status: 1,
    }).then((objAccountresponse) => {
      let accountresponse = objAccountresponse.data;
      if (
        accountresponse.StatusCode === 200 &&
        checkEmptyVal(accountresponse?.Data?.SubAccountKey) == false
      ) {
        let objParams = {
          MerchantKey: accountresponse.Data.SubAccountKey,
        };
        objParams = { ...objParams, ...objCard };
        axiosPost(`${config.usioPaymentConfig.getTokenUrl}`, objParams)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(null);
      }
    });
  });
};

// Generate a USIO card token from an already-fetched merchant key
// (SubAccountKey). Lets a caller verify the sub-account once (via
// GetPaymentSubAccountDetails) and reuse the key here, instead of usioGetToken
// making its own second lookup.
export const usioGenerateToken = (objCard, merchantKey) => {
  return new Promise((resolve, reject) => {
    if (checkEmptyVal(merchantKey)) {
      resolve(null);
      return;
    }
    let objParams = { MerchantKey: merchantKey, ...objCard };
    axiosPost(`${config.usioPaymentConfig.getTokenUrl}`, objParams)
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const Encrypt = (text) => {
  return CryptoJS.AES.encrypt(text, CryptoJS.enc.Utf8.parse(config.secretKey), {
    iv: CryptoJS.enc.Utf8.parse(config.secretIV),
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  }).toString();
};

export const EncryptBase64WithIV = (text) => {
  const key = CryptoJS.enc.Utf8.parse(config.secretKey);
  const iv = CryptoJS.enc.Utf8.parse(config.secretIV);
  const encrypted = CryptoJS.AES.encrypt(text, key, {
    iv,
    mode: CryptoJS.mode.CBC,
    padding: CryptoJS.pad.Pkcs7,
  });
  const combined = iv.clone().concat(encrypted.ciphertext);
  return CryptoJS.enc.Base64.stringify(combined);
};

export const Decrypt = (data) => {
  try {
    const rawData = CryptoJS.enc.Base64.parse(data);
    const rawBytes = Uint8Array.from(
      rawData.words.flatMap((w) => [
        (w >> 24) & 0xff,
        (w >> 16) & 0xff,
        (w >> 8) & 0xff,
        w & 0xff,
      ]),
    ).slice(0, rawData.sigBytes);

    // AES params
    const key = CryptoJS.enc.Utf8.parse(config.secretKey);
    const ivLength = 16; // AES block size
    const iv = rawBytes.slice(0, ivLength);
    const cipherBytes = rawBytes.slice(ivLength);

    const decrypted = CryptoJS.AES.decrypt(
      {
        ciphertext: CryptoJS.lib.WordArray.create(cipherBytes),
      },
      key,
      {
        iv: CryptoJS.lib.WordArray.create(iv),
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    return decrypted.toString(CryptoJS.enc.Utf8);
  } catch (e) {
    return "";
  }
};

function arrayBufferToCustomBase64(buffer) {
  let binary = "";
  let bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  let base64 = btoa(binary);
  // Replace `+` with `-`, `/` with `_`, and remove padding `=`
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function customBase64ToArrayBuffer(base64) {
  base64 = base64.replace(/-/g, "+").replace(/_/g, "/");
  let padding = base64.length % 4;
  if (padding > 0) {
    base64 += "=".repeat(4 - padding);
  }

  let binary = atob(base64);
  let buffer = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    buffer[i] = binary.charCodeAt(i);
  }
  return buffer.buffer;
}

export async function aesCtrEncrypt(plaintext) {
  plaintext =
    plaintext.length == 1 || plaintext.length == 3
      ? plaintext + ":a@w"
      : plaintext.length == 2
        ? plaintext + ":aw"
        : plaintext;

  const encoder = new TextEncoder();
  const keyData = await crypto.subtle.importKey(
    "raw",
    encoder.encode(config.secretKey),
    { name: "AES-CTR" },
    false,
    ["encrypt", "decrypt"],
  );
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-CTR", counter: encoder.encode(config.secretIV), length: 64 },
    keyData,
    encoder.encode(plaintext),
  );
  return arrayBufferToCustomBase64(encrypted);
}

export async function aesCtrDecrypt(ciphertextBase64) {
  try {
    const encoder = new TextEncoder();
    const keyData = await crypto.subtle.importKey(
      "raw",
      encoder.encode(config.secretKey),
      { name: "AES-CTR" },
      false,
      ["decrypt"],
    );
    const encrypted = customBase64ToArrayBuffer(ciphertextBase64);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-CTR", counter: encoder.encode(config.secretIV), length: 64 },
      keyData,
      encrypted,
    );
    return new TextDecoder().decode(decrypted);
  } catch {
    return "";
  }
}

const Base62Chars =
  "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
function base62Encode(input) {
  let source = Array.from(input);
  let result = "";

  while (source.length > 0 && source.some((b) => b !== 0)) {
    let quotient = [];
    let remainder = 0;

    for (let b of source) {
      let accumulator = (remainder << 8) + b;
      let digit = Math.floor(accumulator / 62);
      remainder = accumulator % 62;

      if (quotient.length > 0 || digit > 0) quotient.push(digit);
    }

    result = Base62Chars[remainder] + result;
    source = quotient;
  }

  return result.length === 0 ? "0" : result;
}
function base62Decode(input) {
  let bytes = [0];

  for (let c of input) {
    const value = Base62Chars.indexOf(c);
    if (value < 0) throw new Error("Invalid Base62 character");

    let carry = value;

    for (let i = bytes.length - 1; i >= 0; i--) {
      const accumulator = bytes[i] * 62 + carry;
      bytes[i] = accumulator & 0xff;
      carry = accumulator >> 8;
    }

    while (carry > 0) {
      bytes.unshift(carry & 0xff);
      carry >>= 8;
    }
  }

  return new Uint8Array(bytes);
}

export async function aesCtrEncryptNoSpecialChars(text) {
  try {
    const base64 = await aesCtrEncrypt(text);
    const bytes = customBase64ToArrayBuffer(base64);
    return base62Encode(new Uint8Array(bytes));
  } catch (e) {
    return "";
  }
}

export async function aesCtrDecryptNoSpecialChars(text) {
  const encryptedBytes = base62Decode(text);
  const base64 = arrayBufferToCustomBase64(encryptedBytes.buffer);
  return await aesCtrDecrypt(base64);
}

export const formatBytes = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const result = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${result} ${sizes[i]}`;
};

export const trimCommas = (str) => {
  return str.replace(/^,+|,+$/g, "");
};

export async function convertImageToBase64(url) {
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
}

export function isInt(value) {
  return typeof value === "number" && Number.isInteger(value);
}

export function lastNChars(number, maskcount = 4) {
  if (!number) return "";
  const str = number.toString();
  if (maskcount === -1) return str.length;
  return str.slice(-maskcount);
}

export function maskNumber(number, maskcount = 4) {
  if (checkEmptyVal(number)) return "";
  let str = number.toString();
  return maskcount == -1
    ? str.replace(/\d/g, "•")
    : str.slice(0, -maskcount).replace(/\d/g, "•") + str.slice(-maskcount);
}

export function UrlWithoutParam(page) {
  let url = page.path;
  switch (page.path.toLowerCase()) {
    case routeNames.signup.path.toLowerCase():
      url = routeNames.signup.path.replace("/:id", "");
      break;
    case routeNames.signin.path.toLowerCase():
      url = routeNames.signin.path.replace("/:id", "");
      break;
    default:
      url = page.path;
      break;
    case routeNames.connections.path.toLowerCase():
      url = routeNames.connections.path.replace("/:profile", "");
      break;
    case routeNames.createprofile.path.toLowerCase():
      url = routeNames.createprofile.path.replace("/:profiletype", "");
      break;
  }
  return url;
}

export function getKeyByValue(obj, value) {
  return Object.entries(obj).find(([key, val]) => val === value)?.[0];
}

export function getObjKeyAndValue(obj, value) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
  const entries = Object.entries(obj);
  if (!entries.length) return null;
  if (value !== undefined) {
    const match = entries.find(([key, val]) => val === value);
    return match ? { key: match[0], value: match[1] } : null;
  }
  const [firstKey, firstValue] = entries[0];
  return { key: firstKey, value: firstValue };
}

export function getPlurals(value) {
  return value + "s";
}

export function getCityStateCountryZipFormat(
  objData,
  isSeperator = true,
  customPropsNames = [],
) {
  let parts = [];
  if (customPropsNames.length == 0) {
    parts = [
      objData?.City || "",
      objData?.StateShortName || objData?.State || "",
      objData?.CountryShortName || objData?.Country || "",
      objData?.Zip || "",
    ];
  } else {
    parts = [
      objData?.[customPropsNames[0]] || "",
      objData?.[customPropsNames[1]] || objData?.[customPropsNames[2]] || "",
      objData?.[customPropsNames[3]] || objData?.[customPropsNames[4]] || "",
      objData?.[customPropsNames[5]] || "",
    ];
  }
  parts = parts.filter(Boolean);
  const result = isSeperator ? parts.join(", ") : parts.join(" ").trim();
  return result ? `${result}` : "";
}

// export const maskSensitive = (value = "") => {
//   if (!value) return "";
//   const val = value.toString();
//   if (val.length <= 4) return val;
//   const masked = "x".repeat(val.length - 4) + val.slice(-4);
//   return masked;
// };

export function setProfileDescText(profileTypeId) {
  if (profileTypeId == config.userProfileTypes.Owner) {
    return " Manages property and invites agents or tenants.";
  } else if (profileTypeId == config.userProfileTypes.Agent) {
    return " Handles daily operations and tenant relations.";
  } else if (profileTypeId == config.userProfileTypes.Tenant) {
    return " Occupies and manages leased properties.";
  } else {
    return "";
  }
}

export function setUserAccountTypeDescText(profileTypeId) {
  if (profileTypeId == config.userProfileAccountTypes.Personal) {
    return " For individuals managing own properties, renting a home, or searching to buy or lease.";
  } else if (profileTypeId == config.userProfileAccountTypes.Business) {
    return " For real estate professionals managing properties, clients, and listings.";
  } else {
    return "";
  }
}

export function getInitials(fullName) {
  // if (!fullName) return "";
  // const parts = fullName.trim().split(" ").filter(Boolean);
  // const firstInitial = parts[0]?.[0]?.toUpperCase() || "";
  // const lastInitial = parts[1]?.[0]?.toUpperCase() || "";
  // return firstInitial + lastInitial;
  if (!fullName) return "";
  const parts = fullName.trim().split(" ").filter(Boolean);
  const first = parts[0] || "";
  const second = parts[1] || "";
  if (second) {
    return (first[0]?.toUpperCase() || "") + (second[0]?.toUpperCase() || "");
  }
  // If only one part, take first two characters
  return first.slice(0, 2).toUpperCase();
}

export function getFullName(firstname, lastname) {
  return [firstname, lastname].filter((x) => x && x.trim() !== "").join(" ");
}

export function getUserDisplayName(user, fallback = "") {
  const firstName = user?.FirstName ?? user?.firstname ?? user?.firstName ?? "";
  const lastName = user?.LastName ?? user?.lastname ?? user?.lastName ?? "";
  const name = user?.Name ?? user?.name ?? user?.customlabel ?? "";
  const email = user?.Email ?? user?.email ?? "";

  return (
    getFullName(firstName, lastName) ||
    name?.toString().trim() ||
    email?.toString().trim() ||
    fallback
  );
}

export function getConnectionsUrls(pt) {
  if (checkEmptyVal(pt)) {
    return UrlWithoutParam(routeNames.connections);
  }
  return routeNames.connections.path.replace(
    ":profile",
    getKeyByValue(config.userProfileTypes, pt) + "s",
  );
}

export function splitName(fullName) {
  if (!fullName) return { firstname: "", lastname: "" };

  const parts = fullName.trim().split(/\s+/); // split on one or more spaces
  const firstname = parts[0] || "";
  const lastname = parts.slice(1).join(" ") || "";

  return { firstname, lastname };
}

export function getDefaultIfEmpty(txt, fallback = "N/A") {
  if (
    checkEmptyVal(txt) ||
    txt === "undefined undefined" ||
    txt === "undefined"
  ) {
    return fallback;
  }
  return txt;
}

export function setAccordion(jq) {
  if (document.querySelector(".bb-accordion") !== null) {
    jq(".ac-toggle").on("click", function (e) {
      e.preventDefault();

      var $this = jq(this);

      if ($this.hasClass("active") && $this.next().hasClass("show")) {
        $this.removeClass("active");
        $this.next().removeClass("show");
        $this.next().slideUp(350);
      } else {
        // Check accordion type: for single item open
        if ($this.parent().parent().hasClass("ac-single-show")) {
          $this
            .parent()
            .parent()
            .find(".ac-card .ac-toggle")
            .removeClass("active");
          $this
            .parent()
            .parent()
            .find(".ac-card .ac-collapse")
            .removeClass("show");
          $this.parent().parent().find(".ac-card .ac-collapse").slideUp(350);
          $this.addClass("active");
          $this.next().addClass("show");
          $this.next().slideDown(350);
        }

        // Check accordion type: for group item open
        else if ($this.parent().parent().hasClass("ac-group-show")) {
          $this.addClass("active");
          $this.next().addClass("show");
          $this.next().slideDown(350);
        }

        // Default if not use any accordion type
        else {
          $this
            .parent()
            .parent()
            .find(".ac-card .ac-toggle")
            .removeClass("active");
          $this
            .parent()
            .parent()
            .find(".ac-card .ac-collapse")
            .removeClass("show");
          $this.parent().parent().find(".ac-card .ac-collapse").slideUp(350);
          $this.addClass("active");
          $this.next().addClass("show");
          $this.next().slideDown(350);
        }
      }
    });
  }
}

export function GenerateDdlNumbersList(min, max) {
  const list = [];
  for (let i = min; i <= max; i++) {
    list.push({ Id: i.toString(), Text: i.toString() });
  }
  return list;
}

export function GenerateRblYesNo() {
  return [
    { Id: "Y", Text: "Yes" },
    { Id: "N", Text: "No" },
  ];
}

export function GetYesNoText(id) {
  if (!id) return "";
  const normalizedId = id.toString().trim().toUpperCase();
  return normalizedId === "Y" ? "Yes" : normalizedId === "N" ? "No" : "";
}

export const GetUCGridProfilesSection = (label, profile, config) => {
  return (
    <div className="text-center">
      <div className="font-12 font-500 text-primary">
        <i
          className={`fa ${
            profile?.Status === config.userConnectionStatusTypes.Joined
              ? "fa-user-check"
              : "fa-user"
          } pe-1`}
        />
        {label}
        {/* Tooltip (Request Type) */}
        {profile.InviteeId > 0 && (
          <i
            className={`mdi font-14 min-w-16px w-16px gr-badge-pill px-1 ${
              profile.RequestTypeId == config.userConnectionRequestTypes.Sent
                ? "gr-badge-pill-suc mdi-arrow-right"
                : "gr-badge-pill-error mdi-arrow-left"
            } nocnt bo-0 bg-none py-0`}
          >
            <div
              className={`ctooltip opa9 shadow ${
                profile.RequestTypeId == config.userConnectionRequestTypes.Sent
                  ? "bg-primary"
                  : "bg-danger"
              }`}
              style={{ top: "20px" }}
            >
              {profile.RequestType}
            </div>
          </i>
        )}
      </div>

      {/* Status Badge */}
      {profile?.Status === config.userConnectionStatusTypes.Joined ? (
        <div className="d-flex justify-content-center align-items-center gap-1">
          <i className="fa fa-circle font-8px text-primary pt -1"></i>
          <span className="font-12 font-500 text-primary">
            {profile.StatusDisplay}
          </span>
        </div>
      ) : (
        <div
          className={`badge badge-pill gr-badge-pill gr-badge-pill-no-icon gr-badge-pill-fluid ${
            profile?.Status === config.userConnectionStatusTypes.Pending
              ? "gr-badge-pill-warning"
              : profile?.Status === config.userConnectionStatusTypes.Rejected ||
                  profile?.Status ===
                    config.userConnectionStatusTypes.Terminated
                ? "gr-badge-pill-error"
                : profile?.Status ===
                    config.userConnectionStatusTypes.NotInvited
                  ? "gr-badge-pill-info"
                  : ""
          }`}
        >
          {profile?.StatusDisplay ?? ""}
        </div>
      )}
    </div>
  );
};

export const formatModalMessage = (template, values) => {
  let msg = template;
  Object.keys(values).forEach((key) => {
    msg = msg.replace(`{${key}}`, `@@${key}@@`);
  });

  const parts = msg.split(/(@@.*?@@)/g);
  return (
    <div>
      {parts.map((part, idx) => {
        if (part.startsWith("@@") && part.endsWith("@@")) {
          const key = part.replace(/@@/g, "");
          return (
            <span key={`modp-${idx}`} className="font-500 font-14">
              {values[key]}
            </span>
          );
        }
        return <span key={`modp-${idx}`}>{part}</span>;
      })}
    </div>
  );
};

export const getPreviewInvoiceSession = () => {
  let data = getsessionStorageItem(SessionStorageKeys.PreviewInvoiceId, "{}");
  data = JSON.parse(data);
  return {
    invoiceId: parseInt(data?.id ?? 0, 10),
    action: data?.action ?? "create",
  };
};

export const getDocumentsFileIcon = (ext = "") => {
  const cleanExt = ext.replace(".", "").toLowerCase();
  for (const type of Object.values(config.documentFileTypes)) {
    if (type.extensions.includes(cleanExt)) {
      return type.icon;
    }
  }
  return "fa-file";
};

export const buildDocumentFilesAcceptObject = () => {
  const accept = {};
  Object.values(config.documentFileTypes).forEach((type) => {
    type.extensions.forEach((ext) => {
      accept["application/octet-stream"] =
        accept["application/octet-stream"] || [];
      accept["application/octet-stream"].push(`.${ext}`);
    });
  });
  return accept;
};

// Get safe numeric percentage
export function getSafePercentage(value) {
  const num = parseFloat(value);

  if (isNaN(num) || num < 0) return 0;

  return num;
}

// Calculate total share for dynamic members
export function calculateTotalShare(memberdivs, formData) {
  let total = 0;

  for (let i = 0; i < memberdivs.length; i++) {
    const idx = i + 1;

    total += getSafePercentage(formData[`txtmemsharepercentage${idx}`]);
  }

  return total;
}

export function isValidTotalShare(memberdivs, formData) {
  return calculateTotalShare(memberdivs, formData) <= 100;
}

export function isExactTotalShare(memberdivs, formData) {
  return calculateTotalShare(memberdivs, formData) === 100;
}

export function closebsDropdown(e) {
  let $ = window.$;
  let $dropdown = $(e?.target).closest(".gr-action");
  let $closestBtn = $dropdown.find(".gr-action-btn");
  if ($closestBtn.length) {
    $closestBtn.attr("aria-expanded", "false");
    let $menu = $dropdown.find(".gr-action-menu");
    if ($menu.length) {
      $menu.stop(true, true).slideUp(300);
    }
  }
}

export const extractFileExtension = (name = "") => {
  if (!name.includes(".")) return "";
  return name.split(".").pop().toLowerCase();
};
